import React, { useRef } from "react";
import { Portal } from "components/portal/portal";
import { useClickOutside } from "hooks/use-click-outside/use-click-outside";
import { useLockBodyScroll } from "hooks/use-lock-body-scroll/use-lock-body-scroll";
import { Icon } from "design-system/components/icon/icon";
import {
  Overlay,
  ModalContainer,
  ModalHeader,
  ModalHeading,
  CloseButton
} from "./modal.styles";
import type { ModalSize } from "./modal.styles";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  closeOnOutsideClick?: boolean;
  heading?: string;
  closeIcon?: boolean;
  size?: ModalSize;
  stickyHeader?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  closeOnOutsideClick = true,
  heading,
  closeIcon = true,
  size = "md",
  stickyHeader = false
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => {
    if (closeOnOutsideClick) onClose();
  });

  useLockBodyScroll(isOpen);

  if (!isOpen) return null;

  const showHeader = heading || closeIcon;

  return (
    <Portal>
      <Overlay />
      <ModalContainer ref={ref} $size={size}>
        {showHeader && (
          <ModalHeader $hasHeading={!!heading} $sticky={stickyHeader}>
            {heading && <ModalHeading>{heading}</ModalHeading>}
            {closeIcon && (
              <CloseButton onClick={onClose}>
                <Icon name="action-close" />
              </CloseButton>
            )}
          </ModalHeader>
        )}
        {children}
      </ModalContainer>
    </Portal>
  );
};
